/**
 * Admin account management: create an account, reset a password, delete an account.
 *
 * Creating or resetting shows the generated password exactly once. The server keeps only a hash, so
 * if the admin closes the notice without copying it, the fix is another reset.
 */

import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  changeAccountTier,
  createAccount,
  deleteAccount,
  fetchAccounts,
  resetAccountPassword,
  setAccountMinecraft,
} from "../api";
import { formatTimestamp } from "../format";
import { confirmDialog } from "./Dialog";
import type { IssuedPassword, Tier } from "../types";

/**
 * The Minecraft account a staff account acts as for player-profile actions (bans, rollbacks…). The
 * game checks permission by that account's in-game rank. Admin-set only.
 */
function MinecraftLinkCell({
  accountId,
  link,
  csrfToken,
  onSaved,
}: {
  accountId: string;
  link: { uuid: string; name: string } | null;
  csrfToken: string;
  onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(link?.name ?? "");
  const [uuid, setUuid] = useState(link?.uuid ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = async (clear: boolean): Promise<void> => {
    setError(null);
    try {
      await setAccountMinecraft(accountId, clear ? "" : uuid.trim(), clear ? "" : name.trim(), csrfToken);
      setEditing(false);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save.");
    }
  };

  if (!editing) {
    return (
      <span className="mc-link">
        {link ? <b>{link.name}</b> : <span className="users-note">Not linked</span>}{" "}
        <button className="btn" type="button" onClick={() => setEditing(true)}>
          {link ? "Change" : "Link"}
        </button>
      </span>
    );
  }
  return (
    <span className="mc-link mc-link-edit">
      <input className="input" placeholder="Minecraft name" value={name} maxLength={16} onChange={(e) => setName(e.target.value)} aria-label="Minecraft name" style={{ width: "14ch" }} />
      <input className="input mono" placeholder="UUID (with dashes)" value={uuid} maxLength={36} onChange={(e) => setUuid(e.target.value)} aria-label="Minecraft UUID" style={{ width: "34ch" }} />
      <button className="btn" data-variant="primary" type="button" onClick={() => void save(false)}>Save</button>
      {link && <button className="btn" type="button" onClick={() => void save(true)}>Unlink</button>}
      <button className="btn" type="button" onClick={() => setEditing(false)}>Cancel</button>
      {error && <span className="login-error">{error}</span>}
    </span>
  );
}

export function UsersPanel({
  csrfToken,
  currentUserId,
}: {
  csrfToken: string;
  currentUserId: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: fetchAccounts });

  const [username, setUsername] = useState("");
  const [tier, setTier] = useState<Tier>("viewer");
  const [issued, setIssued] = useState<(IssuedPassword & { reset: boolean }) | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ["accounts"] });

  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const create = (event: FormEvent): void => {
    event.preventDefault();
    void run(async () => {
      const result = await createAccount(username.trim(), tier, csrfToken);
      setIssued({ ...result, reset: false });
      setCopied(false);
      setUsername("");
      await refresh();
    });
  };

  const reset = async (id: string, name: string): Promise<void> => {
    const ok = await confirmDialog({
      title: `Reset ${name}'s password?`,
      message: "They will be signed out straight away and need the new temporary password to get back in.",
      confirmLabel: "Reset password",
    });
    if (!ok) return;
    void run(async () => {
      const result = await resetAccountPassword(id, csrfToken);
      setIssued({ ...result, reset: true });
      setCopied(false);
      await refresh();
    });
  };

  const changeTier = async (id: string, name: string, next: Tier): Promise<void> => {
    const ok = await confirmDialog({
      title: `Change ${name} to ${next}?`,
      message: `They will be signed out and see the ${next} pages when they sign back in.`,
      confirmLabel: `Make ${next}`,
    });
    if (!ok) return;
    void run(async () => {
      await changeAccountTier(id, next, csrfToken);
      await refresh();
    });
  };

  const remove = async (id: string, name: string): Promise<void> => {
    const ok = await confirmDialog({
      title: `Delete ${name}?`,
      message: "Their account is removed and they are signed out immediately. This cannot be undone.",
      confirmLabel: "Delete account",
      danger: true,
    });
    if (!ok) return;
    void run(async () => {
      await deleteAccount(id, csrfToken);
      await refresh();
    });
  };

  const canManage = accounts.data?.canManage ?? false;

  return (
    <section className="users-panel">
      <h2>Users</h2>

      {canManage ? (
        <form className="filterbar" onSubmit={create}>
          <input
            className="input"
            placeholder="New username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            aria-label="New username"
            style={{ width: "20ch" }}
          />
          <select
            className="input"
            value={tier}
            onChange={(event) => setTier(event.target.value as Tier)}
            aria-label="Tier"
          >
            <option value="viewer">viewer</option>
            <option value="staff">staff</option>
            <option value="admin">admin</option>
          </select>
          <button className="btn" data-variant="primary" type="submit" disabled={busy || !username.trim()}>
            Create user
          </button>
        </form>
      ) : (
        accounts.data && (
          <p className="users-note">
            The staff_users table is unavailable, so only the accounts in .env can sign in. Check the
            server log and sql/grants.sql.
          </p>
        )
      )}

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      {issued && (
        <div className="issued-password" role="status">
          <div>
            {issued.reset ? "New temporary password for " : "Created "}
            <b>{issued.user.username}</b>
            {issued.reset ? "" : ` (${issued.user.tier})`}. Send them this password — it will not be
            shown again. They will be asked to change it when they sign in.
          </div>
          <div className="issued-row">
            <code>{issued.password}</code>
            <button
              className="btn"
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(issued.password);
                  setCopied(true);
                } catch {
                  setCopied(false);
                }
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
            <button className="btn" type="button" onClick={() => setIssued(null)}>
              Done
            </button>
          </div>
        </div>
      )}

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Tier</th>
              <th>Status</th>
              <th>Created</th>
              <th>Minecraft</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accounts.isLoading && (
              <tr>
                <td colSpan={6} className="empty">
                  Loading…
                </td>
              </tr>
            )}
            {accounts.data?.users.map((account) => (
              <tr key={account.id}>
                <td>{account.username}</td>
                <td>
                  {account.source === "db" && account.id !== currentUserId ? (
                    <select
                      className="input"
                      value={account.tier}
                      disabled={busy}
                      aria-label={`Tier for ${account.username}`}
                      onChange={(event) => void changeTier(account.id, account.username, event.target.value as Tier)}
                    >
                      <option value="viewer">viewer</option>
                      <option value="staff">staff</option>
                      <option value="admin">admin</option>
                    </select>
                  ) : (
                    <span className="mono">{account.tier}</span>
                  )}
                </td>
                <td>
                  {account.source === "env"
                    ? "Defined in .env"
                    : account.mustChangePassword
                      ? "Waiting for first sign-in"
                      : "Active"}
                </td>
                <td className="mono">
                  {account.createdAt ? formatTimestamp(account.createdAt) : "-"}
                  {account.createdBy && account.source === "db" ? ` by ${account.createdBy}` : ""}
                </td>
                <td>
                  <MinecraftLinkCell
                    accountId={account.id}
                    link={account.minecraft ?? null}
                    csrfToken={csrfToken}
                    onSaved={() => void refresh()}
                  />
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {account.id === currentUserId ? (
                    <span className="users-note">You</span>
                  ) : (
                    account.source === "db" && (
                      <>
                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          onClick={() => void reset(account.id, account.username)}
                        >
                          Reset password
                        </button>{" "}
                        <button
                          className="btn"
                          type="button"
                          disabled={busy}
                          onClick={() => void remove(account.id, account.username)}
                        >
                          Delete
                        </button>
                      </>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
